import { getFileUrl } from '@/clients/http'
import { Image as ImageIcon, Plus, X } from 'lucide-react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useState } from 'react'

import RichTextEditor from '@/components/ui/RichTextEditor'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'

import { SelectInput } from '@/components/atoms/inputs/SelectInput'
import { SwitchInput, TextInput } from '@/components/atoms'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { MediaGalleryModal } from '@/components/media/MediaGalleryModal'

import { DISCOUNT_TYPE_OPTIONS, PRODUCT_STATUS_OPTIONS } from '@/constants/statuses'
import { BrandModel, ProductCategoryModel } from '@/types'
import { ProductModel } from '@/types/model/Product'

import { productFieldsConfig } from './productFieldsConfig'

const PRICE_TYPE_OPTIONS = [
  { value: 'fixed', label: 'Fixed price' },
  { value: 'variable', label: 'Cashier enters the price' },
]
import { CategoryTreeSelect } from '../../categories/CategoryTreeSelect'
import { GenerateTextButton } from '@/components/assistant/GenerateTextButton'
import { useChangedClass } from '@/components/assistant/useProductProposal'

interface GeneralSectionProps {
  form: UseFormReturn<ProductModel.ProductFormData>
  attributes: ProductModel.IAttribute[]
  attributesLoading: boolean
  categories: ProductCategoryModel.ProductCategory[]
  categoriesLoading: boolean
  brands: BrandModel.Brand[]
  brandsLoading: boolean
}

export const GeneralSection = ({
  form,
  categories,
  categoriesLoading,
  brands,
  brandsLoading,
}: GeneralSectionProps) => {
  const { control, watch, setValue, getValues } = form
  const formValues = watch()
  const { fields } = productFieldsConfig({ formValues })
  const [isThumbnailGalleryOpen, setIsThumbnailGalleryOpen] = useState(false)
  const thumbnailUrl = watch('thumbnailUrl')
  const hasPos = useFeatureFlags().isFeatureEnabled('pos')
  const [tagInput, setTagInput] = useState('')
  // Fields the assistant filled in, tinted while the merchant reviews them.
  const changed = useChangedClass()

  const handleAddTag = (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmedTag = tagInput.trim()
    if (!trimmedTag) return

    const currentTags = getValues('tags') || []
    if (!currentTags.includes(trimmedTag)) {
      setValue('tags', [...currentTags, trimmedTag], { shouldDirty: true })
    }
    setTagInput('')
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-6">
      <div className="min-w-0 space-y-4">
        <Card>
          <CardContent className="space-y-4 pt-4">
            <div className={changed('name')}>
              <TextInput
                control={control}
                name="name"
                label="Title"
                placeholder="e.g. Premium wireless headphones"
                rules={fields.name.rules}
              />
            </div>
            <div className={changed('summary')}>
              <TextInput
                control={control}
                name="summary"
                label="Summary"
                multiline
                rows={4}
                placeholder="Short summary of the product"
                rules={fields.summary.rules}
              />
            </div>
            <Controller
              control={control}
              name="description"
              rules={fields.description.rules}
              render={({ field, fieldState: { error } }) => (
                <div className={changed('description', 'space-y-1.5')}>
                  <div className="flex items-center justify-between">
                    <Label>Description</Label>
                    <GenerateTextButton
                      field="description"
                      title={formValues.name ?? ''}
                      current={field.value}
                      onText={(text) => field.onChange(text)}
                    />
                  </div>
                  <RichTextEditor
                    placeholder="Describe your product..."
                    className="min-h-[150px]"
                    value={field.value}
                    onChange={field.onChange}
                  />
                  {error && <p className="text-sm text-error">{error.message}</p>}
                </div>
              )}
            />
            <Controller
              control={control}
              name="deliveryReturnPolicy"
              rules={fields.deliveryReturnPolicy.rules}
              render={({ field, fieldState: { error } }) => (
                <div className={changed('deliveryReturnPolicy', 'space-y-1.5')}>
                  <Label>Delivery and return policy</Label>
                  <RichTextEditor
                    placeholder="Describe your delivery and return policy..."
                    className="min-h-[150px]"
                    value={field.value}
                    onChange={field.onChange}
                  />
                  {error && <p className="text-sm text-error">{error.message}</p>}
                </div>
              )}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Media</CardTitle>
          </CardHeader>
          <CardContent className={changed('thumbnailId')}>
            {thumbnailUrl ? (
              <div className="space-y-3">
                <div className="aspect-video w-full overflow-hidden rounded-lg border border-border bg-subdued md:aspect-auto md:h-[220px]">
                  <img
                    src={getFileUrl(thumbnailUrl)}
                    alt="Product thumbnail"
                    className="size-full object-contain"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="secondary" onClick={() => setIsThumbnailGalleryOpen(true)}>
                    Change
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setValue('thumbnailUrl', '')
                      setValue('thumbnailId', '')
                    }}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[140px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-input px-4 py-6 text-center">
                <ImageIcon className="size-5 text-muted-foreground" />
                <Button type="button" variant="secondary" onClick={() => setIsThumbnailGalleryOpen(true)}>
                  Select existing
                </Button>
                <p className="text-sm text-text-2">Choose the product thumbnail from your media library</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tags</CardTitle>
          </CardHeader>
          <CardContent>
            <Controller
              control={control}
              name="tags"
              rules={fields.tags.rules}
              render={({ field, fieldState: { error } }) => (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      aria-label="Add a tag"
                      placeholder="Add a tag"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleAddTag()
                        }
                      }}
                    />
                    <Button type="button" variant="secondary" onClick={() => handleAddTag()}>
                      <Plus />
                      Add
                    </Button>
                  </div>
                  <p className="text-sm text-text-2">Press Enter or select Add to add a tag.</p>

                  {field.value?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {field.value.map((tag: string, index: number) => (
                        <Badge key={index} tone="soft" className="gap-1 pr-1 text-ink">
                          {tag}
                          <button
                            type="button"
                            aria-label={`Remove ${tag}`}
                            className="grid size-4 place-items-center rounded-sm text-text-2 transition-colors hover:bg-selected hover:text-ink"
                            onClick={() => {
                              const newTags = field.value.filter((_: any, i: number) => i !== index)
                              field.onChange(newTags)
                            }}
                          >
                            <X className="size-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  {error && <p className="text-sm text-error">{error.message}</p>}
                </div>
              )}
            />
          </CardContent>
        </Card>
      </div>

      <div className="min-w-0 space-y-4">
        <Card>
          <CardContent className={changed('status', 'pt-4')}>
            <SelectInput
              control={control}
              name="status"
              label="Status"
              placeholder="Select status"
              rules={fields.status.rules}
              options={PRODUCT_STATUS_OPTIONS}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Product organization</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className={changed('category', 'space-y-1.5')}>
              <Label>Category</Label>
              <Controller
                control={control}
                name="category"
                rules={fields.category.rules}
                render={({ field }) => (
                  <CategoryTreeSelect
                    value={field.value}
                    onChange={(val) => field.onChange(val)}
                    selectionMode="product"
                    placeholder={categoriesLoading ? 'Loading categories...' : 'Choose a product category'}
                    disabled={categoriesLoading}
                    defaultLabel={categories.find((c) => c._id === field.value)?.name}
                  />
                )}
              />
              {form.formState.errors.category && (
                <p className="text-sm text-error">{form.formState.errors.category.message}</p>
              )}
            </div>

            <SelectInput
              control={control}
              name="brand"
              label="Brand"
              placeholder={brandsLoading ? 'Loading brands...' : 'Select brand'}
              disabled={brandsLoading}
              options={brands.map((b) => ({ label: b.name, value: b._id }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Discount</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <TextInput
              control={control}
              name="discount"
              label="Amount"
              type="number"
              placeholder="0"
              valueAsNumber
            />
            <SelectInput
              control={control}
              name="discountType"
              label="Discount type"
              placeholder="Select discount type"
              options={DISCOUNT_TYPE_OPTIONS}
            />
          </CardContent>
        </Card>

        {hasPos && (
          <Card>
            <CardHeader>
              <CardTitle>Point of sale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <SelectInput
                control={control}
                name="priceType"
                label="Price at the POS"
                options={PRICE_TYPE_OPTIONS}
              />
              <SwitchInput
                control={control}
                name="soldByUnit"
                label="Sold by weight or length"
                description="The cashier enters a decimal quantity, such as 1.25 kg. Scale labels set it."
              />
            </CardContent>
          </Card>
        )}
      </div>

      <MediaGalleryModal
        open={isThumbnailGalleryOpen}
        onOpenChange={setIsThumbnailGalleryOpen}
        onSelect={(files) => {
          if (files.length > 0) {
            setValue('thumbnailId', files[0].id)
            setValue('thumbnailUrl', files[0].url)
          }
        }}
        multiple={false}
      />
    </div>
  )
}
